/* eslint-disable @typescript-eslint/no-explicit-any */
import { API_CONFIG, getApiUrl } from "../config/apiConfig";
import { handleResponse } from "./apiHandler";

export interface LoginCredentials {
  email: string;
  password: string;
  remember_me?: boolean;
}

export interface SignupData extends LoginCredentials {
  f_name?: string;
  l_name?: string;
  consent: boolean;
}

export interface ForgotData {
  email: string;
}

export interface ResetData {
  token: string;
  new_password: string;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  user: {
    id: string;
    email: string;
    name?: string;
  };
}

/**
 * Get stored JWT token
 */
export const getToken = (): string | null => {
  return localStorage.getItem("auth_token");
};

/**
 * Store JWT token
 */
export const setToken = (token: string): void => {
  localStorage.setItem("auth_token", token);
};

/**
 * Remove JWT token
 */
export const removeToken = (): void => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("jwt_refresh_token");
  localStorage.removeItem("user");
};

/**
 * Get authorization headers
 */
export const getAuthHeaders = (): Record<string, string> => {
  const token = getToken();

  return {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",

    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export const getFileHeaders = (): Record<string, string> => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/*
Get Csv download headers
*/
export const getDownloadHeaders = (): Record<string, string> => {
  const token = getToken();

  return {
    "Cache-Control": "no-cache, no-transform",
    Pragma: "no-cache",
    Expires: "0",
    Accept: "text/csv",

    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/**
 * Login user
 */
export const login = async (
  credentials: LoginCredentials,
): Promise<AuthResponse> => {
  const response = await fetch(getApiUrl(API_CONFIG.endpoints.auth.login), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(credentials),
  });

  const result = await handleResponse(response, "Login");

  // ✅ Success case
  // localStorage.setItem("auth_token", result.value.accessToken);
  // localStorage.setItem("refreshToken", result.value.refreshToken);
  // localStorage.setItem("user", JSON.stringify(result.value));

  const d = result.value;

  console.log(d);

  // ✅ If MFA required → DO NOT store tokens
  if (d.mfa_required) {
    sessionStorage.setItem("mfa_token", d.temp_token);
  } else {
    // ✅ Normal login → store tokens
    localStorage.setItem("auth_token", result.value.accessToken);
    localStorage.setItem("refreshToken", result.value.refreshToken);
    localStorage.setItem("user", JSON.stringify(result.value));
  }

  // return {
  //   value: d,
  //   message: result.message,
  //   status: response.status,
  // };

  return {
    ...result,
    status: response.status,
  } as any;
};

/**
 * Signup new user
 */
export const signup = async (signupData: SignupData): Promise<AuthResponse> => {
  const response = await fetch(getApiUrl(API_CONFIG.endpoints.auth.signup), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(signupData),
  });

  const result = await handleResponse(response, "Signup");

  localStorage.setItem("auth_token", result.value.accessToken);
  localStorage.setItem("refreshToken", result.value.refreshToken);

  return {
    ...result,
    status: response.status,
  } as any;
};

export const resetpassword = async (
  credentials: ResetData,
): Promise<AuthResponse> => {
  const response = await fetch(
    getApiUrl(
      API_CONFIG.endpoints.auth.resetpassword + "?token=" + credentials.token,
    ),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ new_password: credentials.new_password }),
    },
  );

  const result = await handleResponse(response, "Reset Password");

  return {
    ...result,
    status: response.status,
  } as any;
};

export async function forgotpassword(payload: {
  email: string;
}): Promise<AuthResponse> {
  const response = await fetch(
    getApiUrl(API_CONFIG.endpoints.auth.forgotpassword),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );

  const result = await handleResponse(response, "Forgot Password");

  return {
    ...result,
    status: response.status,
  } as any;
}

/**
 * Logout user
 */
export const logout = async (): Promise<void> => {
  const token = getToken();
  if (token) {
    try {
      await fetch(getApiUrl(API_CONFIG.endpoints.auth.logout), {
        method: "POST",
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Logout request failed:", error);
    }
  }
  removeToken();
};

/**
 * Refresh JWT token
 */
export const refreshToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem("jwt_refresh_token");
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await fetch(getApiUrl(API_CONFIG.endpoints.auth.refresh), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    removeToken();
    throw new Error("Token refresh failed");
  }

  const data = await response.json();
  setToken(data.token);
  return data.token;
};

export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
};

export const verifyMfa = async (otp: string, remember_me = false) => {
  const tempToken = sessionStorage.getItem("mfa_token");

  if (!tempToken) {
    throw new Error("MFA session expired. Please login again.");
  }

  const response = await fetch(getApiUrl(API_CONFIG.endpoints.auth.verifyMfa), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tempToken}`, // 🔥 important
    },
    body: JSON.stringify({ otp, remember_me }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "MFA verification failed");
  }

  const d = data.value;

  // ✅ Store real tokens now
  localStorage.setItem("auth_token", d.accessToken);
  localStorage.setItem("refreshToken", d.refreshToken);

  // ✅ cleanup temp token
  sessionStorage.removeItem("mfa_token");

  return {
    value: d,
    message: data.message,
    status: response.status,
  };
};
